//src/app/features/documents/CreateNodesForm.tsx
import { yupResolver } from '@hookform/resolvers/yup';
import useParallelQueries from 'app/hooks/useParallelQueries';
import { ScrollContainer } from 'app/layout/dashboardLayout/BoxLayout';
import {
  IDirectoryNode,
  useDirectoryNodeFormMutation,
} from 'app/services/documents';
import { FolderSolidIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import {
  DocumentAttachment,
  filesValidationSchema,
  MultiFileSelector,
} from 'Components/Buttons/MultiFileUploader';
import { TextInputBasic } from 'Components/Form/Basic/TextInputBasic';
import Label from 'Components/Form/Label';
import { successToast } from 'Components/Notifications';
import { useClosePopup, useCurrentPopup } from 'Components/Popups';
import { ClientBusinessTaskTaxReturnSelect } from 'Components/Selectors/ClientBusinessTaskTaxReturnSelect';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useDocumentsContext } from '.';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const validationSchema = Yup.object().shape({
  title: Yup.string().when('files', {
    is: (files: File[]) => files === undefined || files.length === 0,
    then: Yup.string().required('Documents or folder name is required'),
  }),

  files: filesValidationSchema,
});

export type NodeForm = {
  id?: number;
  title: string;
  description?: string;
  doctype?: string;
  client_id?: number;
  business_id?: number;
  task_id?: number;
  project_id?: number;
  parent_id?: number | null;
  object_type: 'folder' | 'document';
  files?: DocumentAttachment[];
  file?: File;
};

export function useCreateMultipleUploads() {
  const [trigger] = useDirectoryNodeFormMutation();
  const [isLoading, setIsLoading] = useState(false);

  const { registerEndpoints, results, fetchAll, createEndpoint } =
    useParallelQueries<IDirectoryNode>();

  function submit(form: NodeForm) {
    setIsLoading(true);
    const commonFields = {
      business_id: form.business_id,
      client_id: form.client_id,
      parent_id: form.parent_id || null,
      task_id: form.task_id,
      project_id: form.project_id,
    };

    const fileEndpoints = (parentId: number | null) =>
      fetchAll(
        registerEndpoints(
          (form.files || []).map((f) =>
            createEndpoint(f.uuid, () =>
              trigger({
                ...commonFields,
                object_type: 'document',
                parent_id: parentId || null,
                file: f.file,
                title: f.title,
              }).unwrap(),
            ),
          ),
        ),
      );

    const promise = form.title
      ? trigger({
          ...commonFields,
          title: form.title,
          object_type: 'folder',
        })
          .unwrap()
          .then((folder) => fileEndpoints(folder.id))
      : fileEndpoints(form.parent_id || null);

    promise.then((response) => {
      setIsLoading(false);
      return response;
    });

    return promise;
  }

  return [submit, { results, isLoading }] as const;
}

export default function CreateNodesForm() {
  const {
    filters,
    lockClient,
    directory: gridDirectory,
    lockProject,
    lockTask,
    refetchAfterAdd,
    totalCount,
    per_page,
  } = useDocumentsContext();
  const { close: closeModal, contextData } = useCurrentPopup<{
    parent?: IDirectoryNode;
  }>();
  const parent = contextData?.parent;
  let {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    getValues,
    watch,
    control,
  } = useForm<NodeForm>({
    resolver: validationSchema && yupResolver(validationSchema),
    defaultValues: {
      client_id:
        filters.client_id || lockClient?.id || parent?.clients?.[0]?.id,
      business_id: filters.business_id || parent?.business?.id,
      task_id: filters.task_id || parent?.task?.id,
      project_id: filters.project_id,
      parent_id: parent?.id,
      description: '',
    },
  });

  const [multipleUploads, { results, isLoading }] = useCreateMultipleUploads();

  const closePopup = useClosePopup();

  useEffect(() => {
    if (results) {
      const newstate = (getValues('files') || []).map((x) => {
        const { isError, isLoading, isUnitialized, isSuccess } =
          results[x.uuid];
        return {
          ...x,
          isError,
          isLoading,
          isSuccess,
          isUnitialized,
        };
      });
      setValue('files', newstate);
    }
  }, [JSON.stringify(results)]);

  function submit(form: NodeForm) {
    multipleUploads(form).then(({ endpoints, isAllSuccess }) => {
      if (isAllSuccess) {
        form.files &&
          form.files.length > 0 &&
          successToast(`${form.files?.length} documents uploaded `);

        form.title && successToast(`${form.title} folder has been created `);

        refetchAfterAdd(
          form.title ? gridDirectory?.subfolders_count : totalCount,
          per_page,
        );
        closePopup();
      } else {
        const failedEndpointsIds = endpoints
          .filter((e) => e.isError)
          .map((fe) => fe.id);

        setValue(
          'files',
          (form.files || []).filter((f) => failedEndpointsIds.includes(f.uuid)),
        );
        setValue('title', '');
      }
    });

    return;
  }

  const clientId = watch('client_id');
  const businessId = watch('business_id');
  const taskId = watch('task_id');
  const projectId = watch('project_id');

  useEffect(() => {
    setValue('parent_id', parent?.id);
  }, [parent]);

  return (
    <>
      <form
        id="node_form"
        onSubmit={handleSubmit(submit)}
        className="h-full flex-1 w-full grid grid-cols-4 md-max:grid-cols-1 gap-2 overflow-y-auto"
      >
        <div className="col-span-1 p-2 border-gray-100 border-r">
          <ClientBusinessTaskTaxReturnSelect
            clientId={clientId}
            businessId={businessId}
            taskId={taskId}
            projectId={projectId}
            lockProject={lockProject}
            lockClient={lockClient}
            lockTask={lockTask}
            onChange={({ clientId, businessId, taskId, projectId }) => {
              setValue('client_id', clientId);
              setValue('business_id', businessId);
              setValue('task_id', taskId);
              setValue('project_id', projectId);
            }}
          />
        </div>

        <div className="p-2 col-span-3 h-full flex flex-col flex-1 overflow-y-auto">
          <div className="flex w-full text-gray-dark text-sm items-center gap-2">
            <FolderSolidIcon className="w-5 h-5" />
            <span className=" font-semibold ">{parent?.title} </span> /
            <div className="flex-1">
              <TextInputBasic
                register={register}
                name="title"
                type="text"
                placeholder={`Folder Name`}
                errors={errors}
              />
            </div>
          </div>

          <Controller
            name="files"
            control={control}
            render={({ field }) => (
              <ScrollContainer>
                <div className="flex justify-between items-center gap-2 py-3">
                  <Label label="Documents" />

                  <Button
                    type="button"
                    onClick={closeModal}
                    className="max-w-min ml-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    isLoading={isLoading}
                    form="node_form"
                    type="submit"
                    className="max-w-min"
                    theme="green-fill"
                  >
                    Create
                  </Button>
                </div>

                <ScrollContainer>
                  <MultiFileSelector
                    showUpload={false}
                    errors={errors.files}
                    onChange={field.onChange}
                    value={field.value || []}
                  />
                </ScrollContainer>
              </ScrollContainer>
            )}
          />
        </div>
      </form>
    </>
  );
}
